import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { RatingPicker } from '@/components/forms/RatingPicker';
import { useT } from '@/lib/i18n/useT';
import type { XkStationSummary } from '@/lib/api/types';

interface InventoryTabProps {
  summary: XkStationSummary;
}

/**
 * Task 2.5 — READ-ONLY on purpose.
 *
 * The station page is now an event-day surface: operator mode, operator link,
 * responses, station dashboard, and this "what have I got left" view. Deciding
 * what a station carries is planning work, so it moved to the project's plan
 * board, where the right rail edits a station's xotskies in context (task 3.5)
 * and dropping onto an event spreads a pool across its stations (task 3.4).
 *
 * The allocate form and per-row delete that used to live here are gone: mid-
 * event is the wrong moment to be re-planning inventory, and having two places
 * to edit the same numbers meant they drifted.
 */
export function InventoryTab({ summary }: InventoryTabProps) {
  const t = useT();
  const eventId = summary.station.eventId;
  const groupId = summary.event?.eventGroupId;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('stations.detail.allocationsTitle')}</CardTitle>
        <p className="text-xs text-[var(--color-fg-muted)]">
          {t('stations.detail.allocationsReadonly')}{' '}
          {groupId != null && (
            <Link
              to={`/projects/${groupId}?tab=plan&event=${eventId}`}
              className="font-semibold text-[var(--color-accent-ink)] hover:underline"
            >
              {t('stations.detail.allocationsEditLink')}
            </Link>
          )}
        </p>
      </CardHeader>
      <CardContent>
        {summary.inventory.length === 0 ? (
          <p className="text-sm text-[var(--color-fg-muted)]">
            {t('stations.detail.noAllocations')}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
              <tr>
                <th className="py-2 text-left">{t('stations.detail.stockCol')}</th>
                <th className="py-2 text-left">{t('stations.detail.ratingCol')}</th>
                <th className="py-2 text-right">{t('stations.detail.usedCol')}</th>
                <th className="py-2 text-right">{t('stations.detail.availTotalCol')}</th>
              </tr>
            </thead>
            <tbody>
              {summary.inventory.map((a) => (
                <tr key={a.id} className="border-t border-[var(--color-border-subtle)]">
                  <td className="py-3 font-semibold">{a.stockName}</td>
                  <td className="py-3">
                    <RatingPicker readOnly size="sm" value={a.rating ?? 0} />
                  </td>
                  <td className="py-3 text-right tabular-nums">{a.used}</td>
                  <td className="py-3 text-right tabular-nums">
                    {a.available}
                    <span className="text-[var(--color-fg-subtle)]"> / {a.quantity}</span>
                    {a.available === 0 && (
                      <Badge tone="warn" className="ml-2">
                        {t('stations.detail.out')}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[var(--color-border-default)]">
                <td className="py-3 text-sm uppercase text-[var(--color-fg-muted)]" colSpan={2}>
                  {t('stations.detail.totalAllocated')}
                </td>
                <td className="py-3 text-right font-semibold tabular-nums">
                  {summary.inventoryTotals.used}
                </td>
                <td className="py-3 text-right font-semibold tabular-nums">
                  {summary.inventoryTotals.available}
                  <span className="text-[var(--color-fg-subtle)]">
                    {' '}
                    / {summary.inventoryTotals.quantity}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
